import { Module } from '../../types';

export const regexModule3: Module = {
  id: 'module3',
  title: 'Anchors, Boundaries, and Capturing Groups',
  description:
    'Master precise positioning with anchors, word boundaries, and powerful data extraction using capturing groups.',
  duration: '3.5 hours',
  content: `# Anchors, Boundaries, and Capturing Groups

## 🎯 Learning Objectives

By the end of this module, you will:
- Master line anchors (^, $) for precise start/end matching
- Use word boundaries (\\b) to match complete words only
- Create and manipulate capturing groups with parentheses ()
- Access captured groups and use backreferences
- Understand the difference between matching and capturing
- Apply multiline mode effectively for text processing
- Build complex validation patterns combining anchors and groups

## ⚓ Line Anchors: ^ and $

### The Problem: Unwanted Partial Matches

\`\`\`python
import re

# Trying to validate if a string contains ONLY digits
user_input = "abc123def"
pattern = r"\\d+"

if re.search(pattern, user_input):
    print("Valid number!")  # WRONG! This matches the "123" part
else:
    print("Invalid")
\`\`\`

### Start of Line Anchor (^)

The ^ anchor matches the beginning of a line or string.

\`\`\`python
text = """Hello world
Python is great
Hello again"""

# Find lines that start with "Hello"
pattern = r"^Hello"
matches = re.findall(pattern, text, re.MULTILINE)
print(f"Lines starting with 'Hello': {matches}")
# Output: ['Hello', 'Hello']
\`\`\`

### End of Line Anchor ($)

The $ anchor matches the end of a line or string.

\`\`\`python
text = """file.txt
script.py
readme.md"""

# Find lines ending with ".py"
pattern = r"\\.py$"
matches = re.findall(pattern, text, re.MULTILINE)
print(f"Python files: {matches}")
# Output: ['.py']
\`\`\`

### Combining Anchors for Full String Validation

Use both ^ and $ to ensure the pattern matches the entire string:

\`\`\`python
def is_valid_number(text):
    pattern = r"^\\d+$"
    return bool(re.match(pattern, text))

print(is_valid_number("123"))      # True
print(is_valid_number("abc123"))   # False
print(is_valid_number("123def"))   # False
\`\`\`

### Practical Example: Email Validation

\`\`\`python
def is_valid_email_basic(email):
    pattern = r"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
    return bool(re.match(pattern, email))

emails = ["user@example.com", "invalid", "user@domain.co.uk"]
for email in emails:
    valid = is_valid_email_basic(email)
    print(f"{email}: {'Valid' if valid else 'Invalid'}")
\`\`\`

## 🔤 Word Boundaries: \\b

### The Problem with Partial Word Matches

\`\`\`python
text = "The cat in the cathedral loves catnip"
pattern = r"cat"

matches = re.findall(pattern, text)
print(matches)  # ['cat', 'cat', 'cat'] - matches inside words too!
\`\`\`

### Word Boundary \\b

The \\b boundary matches at the edge of a word (between \\w and \\W characters):

\`\`\`python
text = "The cat in the cathedral loves catnip"
pattern = r"\\bcat\\b"

matches = re.findall(pattern, text)
print(matches)  # ['cat'] - only matches the complete word!
\`\`\`

### Practical Application: Variable Name Replacement

\`\`\`python
def safe_variable_rename(code, old_name, new_name):
    pattern = rf"\\b{re.escape(old_name)}\\b"
    return re.sub(pattern, new_name, code)

code = """
count = 0
counter = 1
count_items = count + counter
"""

# Rename 'count' to 'total' without affecting 'counter' or 'count_items'
new_code = safe_variable_rename(code, "count", "total")
print(new_code)
\`\`\`

### Word Boundary Examples

\`\`\`python
text = "JavaScript and Java are different languages"

# Find whole words
pattern = r"\\bJava\\b"
matches = re.findall(pattern, text)
print(f"Whole word 'Java': {matches}")  # ['Java']

# Find words starting with "Java"
pattern = r"\\bJava\\w*"
matches = re.findall(pattern, text)
print(f"Words starting with 'Java': {matches}")  # ['JavaScript', 'Java']
\`\`\`

## 🎯 Capturing Groups: Extracting Data

### Basic Capturing Groups

Parentheses () create capturing groups that store matched text:

\`\`\`python
# Extract name and age from text
text = "John is 25 years old and Mary is 30 years old"
pattern = r"(\\w+) is (\\d+) years old"

matches = re.findall(pattern, text)
print(matches)  # [('John', '25'), ('Mary', '30')]
\`\`\`

### Accessing Groups with Match Objects

\`\`\`python
text = "Email: user@example.com"
pattern = r"Email: ([\\w._%+-]+)@([\\w.-]+)\\.(\\w+)"

match = re.search(pattern, text)
if match:
    print(f"Full match: {match.group(0)}")    # Full match: Email: user@example.com
    print(f"Username: {match.group(1)}")      # Username: user
    print(f"Domain: {match.group(2)}")        # Domain: example
    print(f"TLD: {match.group(3)}")           # TLD: com
    print(f"All groups: {match.groups()}")    # All groups: ('user', 'example', 'com')
\`\`\`

### Named Groups vs Numbered Groups

Named groups make your patterns more readable and maintainable:

\`\`\`python
# Numbered groups
date_pattern = r"(\\d{4})-(\\d{2})-(\\d{2})"
date_text = "Today is 2024-03-15"

match = re.search(date_pattern, date_text)
if match:
    year = match.group(1)
    month = match.group(2)
    day = match.group(3)

# Named groups (more readable)
named_pattern = r"(?P<year>\\d{4})-(?P<month>\\d{2})-(?P<day>\\d{2})"

match = re.search(named_pattern, date_text)
if match:
    parts = match.groupdict()
    print(parts)  # {'year': '2024', 'month': '03', 'day': '15'}
    
    # Access by name
    print(f"Year: {match.group('year')}")
    print(f"Month: {match.group('month')}")
    print(f"Day: {match.group('day')}")
\`\`\`

## 🔄 Backreferences: Using Captured Content

### Finding Repeated Words

Backreferences let you refer to previously captured groups:

\`\`\`python
# Find repeated words
text = "This is is a test test sentence"
pattern = r"\\b(\\w+)\\s+\\1\\b"

matches = re.findall(pattern, text)
print(matches)  # ['is', 'test'] - the repeated words

# Fix repeated words
fixed_text = re.sub(pattern, r"\\1", text)
print(fixed_text)  # "This is a test sentence"
\`\`\`

### HTML Tag Matching

\`\`\`python
# Match opening and closing HTML tags
html = '<p>Content</p><div>More content</div>'
pattern = r"<(\\w+)>.*?</\\1>"

for match in re.finditer(pattern, html):
    print(f"Tag: {match.group(1)}, Content: {match.group()}")
\`\`\`

### Backreferences in Substitutions

\`\`\`python
# Swap first and last names
text = "John Smith, Jane Doe, Bob Johnson"
pattern = r"(\\w+)\\s+(\\w+)"
swapped = re.sub(pattern, r"\\2, \\1", text)
print(f"Swapped: {swapped}")
# Output: Smith, John, Doe, Jane, Johnson, Bob

# Format phone numbers
phones = "Call 5551234567 or 8005551234"
pattern = r"(\\d{3})(\\d{3})(\\d{4})"
formatted = re.sub(pattern, r"(\\1) \\2-\\3", phones)
print(f"Formatted: {formatted}")
# Output: Call (555) 123-4567 or (800) 555-1234
\`\`\`

## 🌍 Multiline Mode

The MULTILINE flag makes ^ and $ match line boundaries instead of string boundaries:

\`\`\`python
log_text = """2024-01-15: Server started
2024-01-15: Error: Database connection failed
2024-01-16: Warning: High memory usage
2024-01-16: Error: Disk space low"""

# Extract error messages
pattern = r"^(\\d{4}-\\d{2}-\\d{2}): (Error: .*)$"
for match in re.finditer(pattern, log_text, re.MULTILINE):
    date = match.group(1)
    error = match.group(2)
    print(f"{date} -> {error}")
\`\`\`

## 🔧 Practical Applications

### Email Validation with Groups

\`\`\`python
def validate_email(email):
    pattern = r"^(?P<local>[a-zA-Z0-9._%+-]+)@(?P<domain>[a-zA-Z0-9.-]+)\\.(?P<tld>[a-zA-Z]{2,})$"
    
    match = re.match(pattern, email)
    if match:
        return {
            'valid': True,
            'local': match.group('local'),
            'domain': match.group('domain'),
            'tld': match.group('tld')
        }
    return {'valid': False}

# Test the function
emails = ["user@example.com", "invalid.email", "test.user@domain.co.uk"]
for email in emails:
    result = validate_email(email)
    print(f"{email}: {result}")
\`\`\`

### Log File Parsing with Groups

\`\`\`python
def parse_log_entry(log_line):
    # Parse Apache Common Log Format
    pattern = r'^(\\S+) \\S+ \\S+ \\[([^\\]]+)\\] "(\\w+) ([^\\s]+) [^"]+)" (\\d+) (\\d+|-)'
    
    match = re.match(pattern, log_line)
    if match:
        return {
            'ip': match.group(1),
            'timestamp': match.group(2),
            'method': match.group(3),
            'path': match.group(4),
            'status': match.group(5),
            'bytes': match.group(6)
        }
    return None

log_line = '192.168.1.1 - - [25/Dec/2023:10:30:15 +0000] "GET /api/users HTTP/1.1" 200 1234'
parsed = parse_log_entry(log_line)
print(parsed)
\`\`\`

### Phone Number Extraction and Formatting

\`\`\`python
def extract_and_format_phones(text):
    # Pattern with capturing groups for different parts
    pattern = r'\\b(?:\\+?1[-\\.\\s]?)?\\(?(\\d{3})\\)?[-\\.\\s]?(\\d{3})[-\\.\\s]?(\\d{4})\\b'
    
    phones = []
    for match in re.finditer(pattern, text):
        area_code = match.group(1)
        exchange = match.group(2)
        number = match.group(3)
        formatted = f"({area_code}) {exchange}-{number}"
        phones.append({
            'original': match.group(0),
            'formatted': formatted,
            'area_code': area_code,
            'exchange': exchange,
            'number': number
        })
    
    return phones

text = "Call me at 555-123-4567 or (800) 555-0199 or +1 234.567.8901"
phones = extract_and_format_phones(text)
for phone in phones:
    print(f"Original: {phone['original']} -> Formatted: {phone['formatted']}")
\`\`\`

## 🔍 Advanced Group Techniques

### Non-Capturing Groups: (?:...)

Sometimes you need grouping for precedence but don't want to capture the content:

\`\`\`python
# With capturing groups (returns tuples)
text = "Visit https://example.com and http://test.org"
pattern = r"(https?://)(\\w+\\.\\w+)"
matches = re.findall(pattern, text)
print(f"With capturing: {matches}")
# Output: [('https://', 'example.com'), ('http://', 'test.org')]

# With non-capturing groups (returns strings)
pattern = r"(?:https?://)(\\w+\\.\\w+)"
matches = re.findall(pattern, text)
print(f"With non-capturing: {matches}")
# Output: ['example.com', 'test.org']
\`\`\`

### Group References in Substitutions

\`\`\`python
# Format dates from MM/DD/YYYY to YYYY-MM-DD
text = "Born on 03/15/1990 and graduated on 05/20/2012"
pattern = r"(\\d{2})/(\\d{2})/(\\d{4})"
formatted = re.sub(pattern, r"\\3-\\1-\\2", text)
print(f"ISO dates: {formatted}")
# Output: Born on 1990-03-15 and graduated on 2012-05-20

# Using named groups in substitutions
pattern = r"(?P<month>\\d{2})/(?P<day>\\d{2})/(?P<year>\\d{4})"
formatted = re.sub(pattern, r"\\g<year>-\\g<month>-\\g<day>", text)
print(f"Named groups: {formatted}")
\`\`\`

## ⚠️ Common Pitfalls and Solutions

### 1. Anchor Confusion

\`\`\`python
# WRONG: Using ^ and $ without MULTILINE flag
text = "Line 1\\nLine 2\\nLine 3"
pattern = r"^Line"
matches = re.findall(pattern, text)
print(f"Without MULTILINE: {matches}")  # Only finds first line

# CORRECT: Use MULTILINE flag for line-by-line matching
matches = re.findall(pattern, text, re.MULTILINE)
print(f"With MULTILINE: {matches}")  # Finds all lines starting with "Line"
\`\`\`

### 2. Escaping in Groups

\`\`\`python
# When working with literal parentheses in patterns
text = "Function call: func(arg1, arg2)"

# WRONG: Parentheses in pattern need escaping
pattern = r"(\\w+)(.*)"  # This captures the function name and everything after

# CORRECT: Escape literal parentheses
pattern = r"(\\w+)\\((.*)\\)"  # This properly captures function name and arguments
match = re.search(pattern, text)
if match:
    print(f"Function: {match.group(1)}, Args: {match.group(2)}")
\`\`\`

### 3. Group Numbering with Non-Capturing Groups

\`\`\`python
text = "Price: $25.99"

# With all capturing groups
pattern1 = r"(\\w+): (\\$)(\\d+)\\.(\\d+)"
match1 = re.search(pattern1, text)
print(f"Dollar amount (group 3): {match1.group(3)}")  # "25"

# With non-capturing group for currency symbol
pattern2 = r"(\\w+): (?:\\$)(\\d+)\\.(\\d+)"
match2 = re.search(pattern2, text)
print(f"Dollar amount (group 2): {match2.group(2)}")  # "25" (numbering shifts!)
\`\`\`

## 🏆 Best Practices

### 1. Use Named Groups for Clarity

\`\`\`python
# Instead of numbered groups
pattern = r"(\\d{4})-(\\d{2})-(\\d{2})"

# Use named groups
pattern = r"(?P<year>\\d{4})-(?P<month>\\d{2})-(?P<day>\\d{2})"
\`\`\`

### 2. Combine Anchors and Boundaries Appropriately

\`\`\`python
# For full string validation
email_pattern = r"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"

# For word matching within text
word_pattern = r"\\b\\w+\\b"

# For line-specific matching
log_pattern = r"^ERROR:.*$"  # with re.MULTILINE
\`\`\`

### 3. Test Group Behavior

\`\`\`python
def test_group_pattern(pattern, test_string):
    match = re.search(pattern, test_string)
    if match:
        print(f"Full match: {match.group(0)}")
        for i in range(1, match.lastindex + 1 if match.lastindex else 1):
            print(f"Group {i}: {match.group(i)}")
        print(f"All groups: {match.groups()}")
    else:
        print("No match found")

# Test your patterns
test_group_pattern(r"(\\w+)@(\\w+)\\.(\\w+)", "user@example.com")
\`\`\`

## 📊 Quick Reference

### Anchors and Boundaries

| Anchor | Matches | Example Usage |
|--------|---------|---------------|
| ^ | Start of line/string | ^Hello matches lines starting with "Hello" |
| $ | End of line/string | \\.py$ matches lines ending with ".py" |
| \\b | Word boundary | \\bcat\\b matches whole word "cat" |
| \\B | Non-word boundary | \\Bcat\\B matches "cat" inside words |

### Group Types

| Type | Syntax | Purpose |
|------|--------|---------|
| Capturing | (pattern) | Captures matched text |
| Non-capturing | (?:pattern) | Groups without capturing |
| Named | (?P<name>pattern) | Captures with a name |

### Backreferences

| Context | Syntax | Example |
|---------|--------|---------|
| In pattern | \\1, \\2, etc. | (\\w+)\\s+\\1 matches repeated words |
| In replacement | \\1, \\2, etc. | re.sub(r"(\\w+) (\\w+)", r"\\2, \\1", text) |
| Named groups | \\g<name> | re.sub(r"(?P<word>\\w+)", r"\\g<word>s", text) |

## 🚀 What's Next?

You've mastered anchors, boundaries, and capturing groups! In Module 4, you'll learn:

- **Alternation**: | (OR operator) for multiple pattern choices
- **Lookahead/Lookbehind**: Advanced position assertions
- **Non-greedy Quantifiers**: Controlling greedy matching behavior
- **Advanced Group Techniques**: Conditional matching and atomic groups
- **Complex Pattern Composition**: Combining all techniques for sophisticated matching

These advanced features will give you expert-level regex capabilities!`,

  exercises: [
    {
      id: 'module3-exercise1',
      title: 'String Anchoring',
      description:
        'Create a pattern using anchors to match strings that contain ONLY digits: ^\\\\d+$',
      type: 'regex',
      difficulty: 'easy',
      pattern: '^\\d+$',
      testCases: [
        { input: '123', expected: true, description: 'Only digits' },
        { input: '456789', expected: true, description: 'Multiple digits only' },
        { input: 'abc123', expected: false, description: 'Starts with letters' },
        { input: '123def', expected: false, description: 'Ends with letters' },
        { input: '12a34', expected: false, description: 'Contains letters in middle' },
        { input: '0', expected: true, description: 'Single digit' },
      ],
      hints: [
        'Use ^ to match the start of the string',
        'Use $ to match the end of the string',
        'Use \\d+ to match one or more digits',
        'Anchors ensure the entire string matches the pattern',
      ],
    },
    {
      id: 'module3-exercise2',
      title: 'Word Boundaries',
      description: 'Create a pattern to match the whole word "cat" only: \\\\bcat\\\\b',
      type: 'regex',
      difficulty: 'easy',
      pattern: '\\bcat\\b',
      testCases: [
        { input: 'cat', expected: true, description: 'Standalone word cat' },
        { input: 'The cat is sleeping', expected: true, description: 'Cat as complete word' },
        { input: 'cathedral', expected: false, description: 'Cat inside another word' },
        { input: 'catnip', expected: false, description: 'Cat as prefix' },
        { input: 'wildcat', expected: false, description: 'Cat as suffix' },
        { input: 'My cat, dog, and bird', expected: true, description: 'Cat with punctuation' },
      ],
      hints: [
        'Use \\b for word boundaries',
        'Word boundaries match at the edge between \\w and \\W characters',
        'This prevents matching "cat" inside larger words',
        'Punctuation and spaces create word boundaries',
      ],
    },
    {
      id: 'module3-exercise3',
      title: 'Basic Capturing Groups',
      description: 'Create a pattern to capture first and last name: (\\\\w+) (\\\\w+)',
      type: 'regex',
      difficulty: 'medium',
      pattern: '(\\w+) (\\w+)',
      testCases: [
        { input: 'John Smith', expected: true, description: 'First and last name' },
        { input: 'Mary Johnson', expected: true, description: 'Another name pair' },
        { input: 'Bob', expected: false, description: 'Only first name' },
        {
          input: 'Alice Cooper Brown',
          expected: true,
          description: 'Three names (matches first two)',
        },
        {
          input: 'Dr. Jane Doe',
          expected: true,
          description: 'Name with title (matches last two words)',
        },
        { input: 'user123 admin456', expected: true, description: 'Alphanumeric names' },
      ],
      hints: [
        'Use parentheses () to create capturing groups',
        'Use \\w+ to match one or more word characters',
        'Use a space to match the literal space between names',
        'Each group captures one name component',
      ],
    },
    {
      id: 'module3-exercise4',
      title: 'Named Capturing Groups',
      description:
        'Create a pattern with named groups for dates: (?P<year>\\\\d{4})-(?P<month>\\\\d{2})-(?P<day>\\\\d{2})',
      type: 'regex',
      difficulty: 'medium',
      pattern: '(?P<year>\\d{4})-(?P<month>\\d{2})-(?P<day>\\d{2})',
      testCases: [
        { input: '2024-03-15', expected: true, description: 'Valid ISO date' },
        { input: '2023-12-31', expected: true, description: 'End of year date' },
        { input: '2024-1-5', expected: false, description: 'Single digit month and day' },
        { input: '24-03-15', expected: false, description: '2-digit year' },
        { input: 'Today is 2024-03-15', expected: true, description: 'Date in text' },
        { input: '2024/03/15', expected: false, description: 'Wrong separator' },
      ],
      hints: [
        'Use (?P<name>pattern) syntax for named groups',
        'Use \\d{4} for exactly 4 digits (year)',
        'Use \\d{2} for exactly 2 digits (month and day)',
        'Use literal hyphens - to separate date parts',
      ],
    },
    {
      id: 'module3-exercise5',
      title: 'Backreferences for Repeated Words',
      description: 'Create a pattern to find repeated words: \\\\b(\\\\w+)\\\\s+\\\\1\\\\b',
      type: 'regex',
      difficulty: 'hard',
      pattern: '\\b(\\w+)\\s+\\1\\b',
      testCases: [
        { input: 'This is is a test', expected: true, description: 'Repeated word "is"' },
        { input: 'The the quick brown fox', expected: true, description: 'Repeated word "the"' },
        { input: 'A test test sentence', expected: true, description: 'Repeated word "test"' },
        { input: 'No repeated words here', expected: false, description: 'No repetition' },
        { input: 'Same same but different', expected: true, description: 'Repeated word "same"' },
        { input: 'Testing testing 123', expected: true, description: 'Repeated word "testing"' },
      ],
      hints: [
        'Use \\b for word boundaries to match complete words',
        'Use (\\w+) to capture the first word',
        'Use \\s+ to match one or more spaces',
        'Use \\1 to reference the first captured group',
        'This finds cases where the same word appears twice in a row',
      ],
    },
    {
      id: 'module3-exercise6',
      title: 'Email Parsing with Groups',
      description:
        'Create a pattern to capture email parts: ([\\\\w._%+-]+)@([\\\\w.-]+)\\\\.(\\\\w+)',
      type: 'regex',
      difficulty: 'hard',
      pattern: '([\\w._%+-]+)@([\\w.-]+)\\.(\\w+)',
      testCases: [
        { input: 'user@example.com', expected: true, description: 'Standard email' },
        {
          input: 'test.user+tag@domain.co.uk',
          expected: true,
          description: 'Complex email format',
        },
        {
          input: 'admin@test-server.org',
          expected: true,
          description: 'Email with hyphen in domain',
        },
        { input: 'Contact: support@company.com', expected: true, description: 'Email in text' },
        { input: 'invalid@', expected: false, description: 'Incomplete email' },
        { input: '@domain.com', expected: false, description: 'Missing local part' },
      ],
      hints: [
        'First group: [\\w._%+-]+ captures local part (username)',
        'Use @ literally for the at symbol',
        'Second group: [\\w.-]+ captures domain name',
        'Use \\. to match literal dot before TLD',
        'Third group: \\w+ captures top-level domain',
      ],
    },
  ],

  quiz: {
    id: 'module3-quiz',
    title: 'Anchors, Boundaries, and Capturing Groups Quiz',
    questions: [
      {
        id: 'q1',
        question: 'What is the difference between ^ and \\\\b in regex?',
        choices: [
          'No difference - they both match the start of text',
          '^ matches start of line/string, \\\\b matches word boundaries',
          '^ is for case sensitivity, \\\\b is for word matching',
          '^ is faster than \\\\b',
        ],
        correctAnswer: 1,
        explanation:
          '^ matches the start of a line or string, while \\b matches word boundaries (the position between word and non-word characters). They serve different positioning purposes.',
      },
      {
        id: 'q2',
        question: 'When should you use both ^ and $ anchors together?',
        choices: [
          'Never - they conflict with each other',
          'Only for multiline text',
          'When you want to match the entire string, not just find a pattern within it',
          'When you want case-insensitive matching',
        ],
        correctAnswer: 2,
        explanation:
          'Using both ^ and $ ensures the pattern matches the entire string from start to end, which is essential for validation tasks where you need to ensure the whole input conforms to a pattern.',
      },
      {
        id: 'q3',
        question: 'What does the pattern \\\\b(\\\\w+)\\\\s+\\\\1\\\\b match?',
        choices: [
          'Any word followed by a space',
          'Words that appear twice in a row',
          'The first word in a sentence',
          'Words with numbers in them',
        ],
        correctAnswer: 1,
        explanation:
          'This pattern uses a backreference (\\1) to match repeated words. It captures a word (\\w+), matches whitespace (\\s+), then matches the same word again using the backreference \\1.',
      },
      {
        id: 'q4',
        question: 'What is the advantage of named groups (?P<name>pattern) over numbered groups?',
        choices: [
          'Named groups are faster to execute',
          'Named groups make patterns more readable and maintainable',
          'Named groups can capture more text',
          'Named groups work with more regex engines',
        ],
        correctAnswer: 1,
        explanation:
          'Named groups make regex patterns more self-documenting and maintainable. Instead of remembering that group 2 is the domain, you can use meaningful names like "domain" which makes code more readable.',
      },
      {
        id: 'q5',
        question: 'What does the MULTILINE flag change about ^ and $ anchors?',
        choices: [
          'Makes them case-insensitive',
          'Makes them match word boundaries instead',
          'Makes them match at line boundaries instead of string boundaries',
          'Makes them match multiple characters',
        ],
        correctAnswer: 2,
        explanation:
          'With the MULTILINE flag, ^ matches the start of each line (not just the string), and $ matches the end of each line (not just the string). This is useful for processing multi-line text.',
      },
      {
        id: 'q6',
        question: 'What is the difference between (pattern) and (?:pattern)?',
        choices: [
          'No difference - they work the same way',
          '(?:pattern) is a capturing group, (pattern) is not',
          '(pattern) captures text for later use, (?:pattern) groups without capturing',
          '(?:pattern) is faster but less reliable',
        ],
        correctAnswer: 2,
        explanation:
          "(pattern) creates a capturing group that stores the matched text for backreferences or extraction. (?:pattern) creates a non-capturing group that groups for precedence or quantifiers but doesn't store the matched text.",
      },
      {
        id: 'q7',
        question:
          'In the substitution re.sub(r"(\\\\w+) (\\\\w+)", r"\\\\2, \\\\1", text), what happens?',
        choices: [
          'Nothing - the pattern is invalid',
          'All words are removed',
          'First and second words in each match are swapped',
          'Words are converted to uppercase',
        ],
        correctAnswer: 2,
        explanation:
          'This pattern captures two words separated by a space, then swaps their order in the replacement. \\2 refers to the second captured group and \\1 refers to the first, effectively swapping "John Smith" to "Smith, John".',
      },
    ],
    passingScore: 5,
  },

  resources: [
    {
      id: 'anchors-guide',
      title: 'Anchors and Boundaries Reference',
      type: 'document',
      url: '/resources/anchors-boundaries.pdf',
      description: 'Complete guide to regex anchors and word boundaries',
    },
    {
      id: 'capturing-groups',
      title: 'Capturing Groups Tutorial',
      type: 'document',
      url: '/resources/capturing-groups.pdf',
      description: 'In-depth tutorial on capturing groups and backreferences',
    },
    {
      id: 'multiline-regex',
      title: 'Multiline Regex Processing',
      type: 'link',
      url: 'https://www.regular-expressions.info/anchors.html',
      description: 'Understanding multiline mode and line anchors',
    },
    {
      id: 'group-examples',
      title: 'Group Patterns Examples',
      type: 'document',
      url: '/resources/group-examples.pdf',
      description: 'Common patterns using capturing groups for data extraction',
    },
  ],
};
